import { useState, useMemo, useEffect } from 'react';
import { calculateShippingRates, usStates } from '../../../utils/shippingRates';
import { getCart } from '../../../utils/cart';
import { supabase } from '../../../lib/supabase';

interface ShippingCalculatorProps {
  productPrice: number;
  productId: string;
}

function addBusinessDays(date: Date, days: number): Date {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const dayOfWeek = result.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      added++;
    }
  }
  return result;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// Zone-based delivery day adjustments
function getDeliveryDays(zone: number) {
  switch (zone) {
    case 1:
      return { standard: { min: 4, max: 6 }, express: { min: 1, max: 2 }, overnight: { min: 1, max: 1 } };
    case 2:
      return { standard: { min: 5, max: 8 }, express: { min: 2, max: 3 }, overnight: { min: 1, max: 1 } };
    case 3:
      return { standard: { min: 7, max: 10 }, express: { min: 3, max: 4 }, overnight: { min: 1, max: 2 } };
    default:
      return { standard: { min: 5, max: 8 }, express: { min: 2, max: 4 }, overnight: { min: 1, max: 1 } };
  }
}

export default function ShippingCalculator({ productPrice, productId }: ShippingCalculatorProps) {
  const [selectedState, setSelectedState] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [productWeights, setProductWeights] = useState<Record<string, number>>({});

  const CUTOFF_HOUR = 14; // 2 PM EST

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch product weights from Supabase
  useEffect(() => {
    async function fetchWeights() {
      const cart = getCart();
      const productIds = [...cart.map(item => item.id.toString()), productId];
      
      const { data, error } = await supabase
        .from('products')
        .select('id, weight_oz')
        .in('id', productIds);
      
      if (!error && data) {
        const weights: Record<string, number> = {};
        for (const product of data) {
          weights[product.id] = product.weight_oz || 8; // Default 8 oz
        }
        setProductWeights(weights);
      }
    }
    
    fetchWeights();
  }, [productId]);

  // Calculate total weight including cart + current product
  const totalWeightOz = useMemo(() => {
    const cart = getCart();
    let weight = 0;
    
    // Add weight from cart items
    for (const item of cart) {
      const itemWeight = productWeights[item.id.toString()] || 8;
      weight += itemWeight * item.quantity;
    }
    
    // Add current product weight
    const currentProductWeight = productWeights[productId] || 8;
    weight += currentProductWeight;
    
    return weight;
  }, [productId, productWeights]);

  const sameDayInfo = useMemo(() => {
    const now = currentTime;
    const estOffset = -5;
    const utcHours = now.getUTCHours();
    const utcMinutes = now.getUTCMinutes();
    const estHours = (utcHours + estOffset + 24) % 24;
    const estMinutes = utcMinutes;

    const isWeekday = (() => {
      const estDay = new Date(now.getTime() + estOffset * 60 * 60 * 1000).getUTCDay();
      return estDay >= 1 && estDay <= 5;
    })();

    if (!isWeekday) {
      return { canProcess: false, message: 'Orders placed on weekends will be processed on Monday', isWeekend: true };
    }

    if (estHours < CUTOFF_HOUR) {
      const remainingHours = CUTOFF_HOUR - estHours - 1;
      const remainingMinutes = 60 - estMinutes;
      const adjustedHours = remainingMinutes === 60 ? remainingHours + 1 : remainingHours;
      const adjustedMinutes = remainingMinutes === 60 ? 0 : remainingMinutes;

      let timeStr = '';
      if (adjustedHours > 0) timeStr += `${adjustedHours}h `;
      timeStr += `${adjustedMinutes}min`;

      return { canProcess: true, message: `Order within ${timeStr} for same-day processing`, timeStr, isWeekend: false };
    }

    return { canProcess: false, message: 'Orders placed now will be processed next business day', isWeekend: false };
  }, [currentTime]);

  const isSameDay = sameDayInfo.canProcess;

  const freeStandardShipping = productPrice >= 200;

  const rates = useMemo(() => {
    if (!selectedState) return null;
    return calculateShippingRates(selectedState, '', productPrice, totalWeightOz);
  }, [selectedState, productPrice, totalWeightOz]);

  const deliveryDates = useMemo(() => {
    if (!rates) return null;
    const today = new Date();
    const days = getDeliveryDays(rates.zone);
    return {
      standard: {
        from: formatDate(addBusinessDays(today, days.standard.min)),
        to: formatDate(addBusinessDays(today, days.standard.max)),
        label: `${days.standard.min}–${days.standard.max} business days`,
      },
      express: {
        from: formatDate(addBusinessDays(today, days.express.min)),
        to: formatDate(addBusinessDays(today, days.express.max)),
        label: `${days.express.min}–${days.express.max} business days`,
      },
      overnight: {
        from: formatDate(addBusinessDays(today, days.overnight.min)),
        to: days.overnight.min === days.overnight.max
          ? ''
          : formatDate(addBusinessDays(today, days.overnight.max)),
        label: days.overnight.min === days.overnight.max ? 'Next business day' : `${days.overnight.min}–${days.overnight.max} business days`,
      },
    };
  }, [rates]);

  const filteredStates = useMemo(() => {
    if (!searchTerm) return usStates;
    const lower = searchTerm.toLowerCase();
    return usStates.filter(
      (s) =>
        s.label.toLowerCase().includes(lower) ||
        s.value.toLowerCase().includes(lower)
    );
  }, [searchTerm]);

  const selectedLabel = usStates.find((s) => s.value === selectedState)?.label || '';

  const handleSelectState = (value: string) => {
    setSelectedState(value);
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="mb-8 rounded-xl border border-gray-200 overflow-hidden">
      {/* Header - Toggle */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-5 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-calculator-line text-[#2E7D32]"></i>
        </div>
        <h3 className="text-sm font-semibold text-gray-900 flex-1 text-left">
          Shipping Cost Calculator
        </h3>
        <div className="w-5 h-5 flex items-center justify-center">
          <i
            className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          ></i>
        </div>
      </button>

      {/* Calculator Body */}
      {isOpen && (
        <div className="px-5 py-4">
          {/* State Selector */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">
              Select your state
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between gap-2 px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-left cursor-pointer hover:border-[#2E7D32] transition-colors focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/20 focus:border-[#2E7D32]"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-gray-400"></i>
                  </div>
                  <span
                    className={`text-sm truncate ${
                      selectedState ? 'text-gray-900 font-medium' : 'text-gray-400'
                    }`}
                  >
                    {selectedLabel || 'Choose a state...'}
                  </span>
                </div>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${
                      isDropdownOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>
              </button>

              {/* Dropdown */}
              {isDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      setSearchTerm('');
                    }}
                  ></div>
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-20 max-h-64 overflow-hidden">
                    {/* Search */}
                    <div className="p-2 border-b border-gray-100">
                      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-search-line text-gray-400 text-sm"></i>
                        </div>
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Search state..."
                          className="flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 outline-none"
                          autoFocus
                        />
                      </div>
                    </div>
                    {/* List */}
                    <div className="overflow-y-auto max-h-48">
                      {filteredStates.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">
                          No state found
                        </div>
                      ) : (
                        filteredStates.map((state) => (
                          <button
                            key={state.value}
                            type="button"
                            onClick={() => handleSelectState(state.value)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left cursor-pointer transition-colors ${
                              selectedState === state.value
                                ? 'bg-[#F1F8E9] text-[#2E7D32]'
                                : 'hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            <span className="text-xs font-mono text-gray-400 w-6">
                              {state.value}
                            </span>
                            <span className="text-sm">{state.label}</span>
                            {selectedState === state.value && (
                              <i className="ri-check-line text-[#2E7D32] ml-auto"></i>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Results */}
          {rates && deliveryDates ? (
            <div className="space-y-0 rounded-lg border border-gray-100 overflow-hidden">
              {/* Zone Badge */}
              <div className="flex items-center gap-2 px-4 py-2 bg-[#F1F8E9] border-b border-[#C8E6C9]">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-2-line text-[#2E7D32] text-sm"></i>
                </div>
                <span className="text-xs font-semibold text-[#2E7D32]">
                  {selectedLabel} — {rates.zoneLabel} (Zone {rates.zone})
                </span>
              </div>

              {/* Same-Day Processing Indicator */}
              <div className={`flex items-center gap-2.5 px-4 py-2.5 border-b ${
                sameDayInfo.canProcess
                  ? 'bg-emerald-50 border-emerald-200'
                  : sameDayInfo.isWeekend
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-amber-50 border-amber-200'
              }`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                  sameDayInfo.canProcess
                    ? 'bg-emerald-100'
                    : sameDayInfo.isWeekend
                      ? 'bg-blue-100'
                      : 'bg-amber-100'
                }`}>
                  <i className={`text-sm ${
                    sameDayInfo.canProcess
                      ? 'ri-timer-flash-line text-emerald-600'
                      : sameDayInfo.isWeekend
                        ? 'ri-calendar-event-line text-blue-600'
                        : 'ri-time-line text-amber-600'
                  }`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold ${
                    sameDayInfo.canProcess
                      ? 'text-emerald-800'
                      : sameDayInfo.isWeekend
                        ? 'text-blue-800'
                        : 'text-amber-800'
                  }`}>
                    {sameDayInfo.canProcess ? (
                      <>
                        <span className="inline-flex items-center gap-1">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                          </span>
                          {sameDayInfo.message}
                        </span>
                      </>
                    ) : (
                      sameDayInfo.message
                    )}
                  </p>
                  <p className={`text-[10px] mt-0.5 ${
                    sameDayInfo.canProcess ? 'text-emerald-600' : sameDayInfo.isWeekend ? 'text-blue-600' : 'text-amber-600'
                  }`}>
                    Cut-off time: 2:00 PM EST, Mon–Fri
                  </p>
                </div>
              </div>

              {/* Standard */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#F1F8E9] flex items-center justify-center flex-shrink-0">
                    <i className="ri-truck-line text-[#2E7D32] text-sm"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">Standard</p>
                    <p className="text-xs text-gray-500">{deliveryDates.standard.label}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {freeStandardShipping ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 line-through">
                          ${rates.standard.toFixed(2)}
                        </span>
                        <span className="text-sm font-bold text-[#2E7D32]">FREE</span>
                      </div>
                    ) : (
                      <span className="text-sm font-bold text-gray-900">
                        ${rates.standard.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-2 ml-11 flex items-center gap-1.5 bg-gray-50 rounded-md px-2.5 py-1.5">
                  <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-[#2E7D32] text-xs"></i>
                  </div>
                  <span className="text-[11px] text-gray-600">
                    Est. delivery: <strong className="text-gray-900">{deliveryDates.standard.from} – {deliveryDates.standard.to}</strong>
                  </span>
                </div>
              </div>

              {/* Express */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#F1F8E9] flex items-center justify-center flex-shrink-0">
                    <i className="ri-rocket-line text-[#2E7D32] text-sm"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-900">Express</p>
                      <span className="text-[9px] font-bold tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                        POPULAR
                      </span>
                      {isSameDay && (
                        <span className="text-[9px] font-bold tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                          <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span></span>
                          SAME-DAY
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{deliveryDates.express.label}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-sm font-bold text-gray-900">
                      ${rates.express.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="mt-2 ml-11 flex items-center gap-1.5 bg-gray-50 rounded-md px-2.5 py-1.5">
                  <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-[#2E7D32] text-xs"></i>
                  </div>
                  <span className="text-[11px] text-gray-600">
                    Est. delivery: <strong className="text-gray-900">{deliveryDates.express.from} – {deliveryDates.express.to}</strong>
                  </span>
                </div>
              </div>

              {/* Overnight */}
              <div className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#F1F8E9] flex items-center justify-center flex-shrink-0">
                    <i className="ri-flashlight-line text-[#2E7D32] text-sm"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-900">Overnight</p>
                      {isSameDay && (
                        <span className="text-[9px] font-bold tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                          <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span></span>
                          SAME-DAY
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{deliveryDates.overnight.label}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-sm font-bold text-gray-900">
                      ${rates.overnight.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="mt-2 ml-11 flex items-center gap-1.5 bg-gray-50 rounded-md px-2.5 py-1.5">
                  <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-[#2E7D32] text-xs"></i>
                  </div>
                  <span className="text-[11px] text-gray-600">
                    Est. delivery: <strong className="text-gray-900">{deliveryDates.overnight.from}{deliveryDates.overnight.to ? ` – ${deliveryDates.overnight.to}` : ''}</strong>
                  </span>
                </div>
              </div>
            </div>
          ) : (
            /* Empty State */
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <i className="ri-map-pin-line text-xl text-gray-300"></i>
              </div>
              <p className="text-sm text-gray-500">
                Select your state to see exact shipping costs
              </p>
            </div>
          )}

          {/* Free shipping note */}
          {rates && !freeStandardShipping && (
            <div className="mt-3 flex items-start gap-2 px-3 py-2 bg-[#FFFDE7] rounded-lg border border-amber-200">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-lightbulb-line text-amber-600 text-sm"></i>
              </div>
              <p className="text-xs text-amber-800">
                Add <strong>${(200 - productPrice).toFixed(2)}</strong> more to your order for{' '}
                <strong>free standard shipping</strong>!
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
